import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Animated, View, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter, useLocalSearchParams } from 'expo-router';

// React Native Reusables Components
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';

// Services & Components
import { authAPI, billingAPI } from '../../services/api';
import SampleReportViewer from '../../components/onboarding/SampleReportViewer';
import OnboardingStepLayout from '../../components/onboarding/OnboardingStepLayout';
import {
  ONBOARDING_COLORS as COLORS,
  ONBOARDING_CONTROL_HEIGHT,
  ONBOARDING_DISCLOSURE_TRANSITION_MS,
  ONBOARDING_FEATURE_DETAIL_MIN_HEIGHT,
  ONBOARDING_RADII,
  ONBOARDING_SPACING,
  ONBOARDING_TYPOGRAPHY,
} from '../../components/onboarding/onboardingTheme';
import { useTranslation } from '../../hooks/useTranslation';
import { ROUTES } from '../../navigation/routes';
import { useAuth } from '@/context/AuthContext';
import { useAccessPolicy } from '../../hooks/useAccessPolicy';
import {
  trackAuthEvent,
  trackFeatureMapItemTapped,
} from '../../services/analytics';
import PlanSummaryCard from '../../components/billing/PlanSummaryCard';
import { hasEffectiveBillingAccess } from '../../utils/billingAccess';
import { useResponsive } from '../../hooks/useResponsive';

// Plan Context Utilities
import {
  type PlanTier,
  getOrInitPlanContext,
} from '../../utils/planContext';

/** Funnel order marker for analytics: tour → intake → plan selection. */
const FUNNEL_VERSION = 'intake_first';

interface OnboardingProps {
  onContinue?: () => void;
}

// Slide types
type SlideType = 'feature' | 'demo' | 'confirmation';

interface Slide {
  type: SlideType;
  icon: keyof typeof MaterialIcons.glyphMap;
  title: string;
  description: string;
  /** Stable key for analytics event payloads (e.g. "transcript", "demo", "methodology"). */
  analyticsKey?: string;
}

type FeatureMapKey =
  | 'voice'
  | 'analysis'
  | 'progress'
  | 'development'
  | 'export'
  | 'library'
  | 'meeting'
  | 'chat';

const FEATURE_MAP_ITEMS: Array<{
  key: FeatureMapKey;
  icon: keyof typeof MaterialIcons.glyphMap;
}> = [
  { key: 'voice', icon: 'mic' },
  { key: 'analysis', icon: 'fact-check' },
  { key: 'progress', icon: 'trending-up' },
  { key: 'development', icon: 'route' },
  { key: 'export', icon: 'picture-as-pdf' },
  { key: 'library', icon: 'folder-special' },
  { key: 'meeting', icon: 'groups' },
  { key: 'chat', icon: 'forum' },
];

/**
 * Enhanced Onboarding Screen — the value tour (part 1 of the funnel).
 *
 * Funnel order: tour → intake questions + AI plan reveal → plan selection /
 * payment → app. This screen NEVER starts a Stripe checkout: any plan context
 * carried over from the landing page is preserved and preselected later on the
 * plan-selection screen, which now comes after the intake.
 *
 * Features:
 * - Plan context awareness (from landing page selection), passive display only
 * - Feature education slides + sample report demo
 * - Routes to the intake wizard on completion
 */
export default function OnboardingScreenEnhanced({ onContinue }: OnboardingProps) {
  const router = useRouter();
  const { t, language } = useTranslation();
  const { billingStatus, refreshAuth } = useAuth();
  const { isPhone } = useResponsive();
  const params = useLocalSearchParams<{ plan?: string | string[] }>();

  // User type checks
  const { isB2C, isSchoolAdmin, canAccessIndividualPaywall, requiresIntake } = useAccessPolicy();
  const shouldShowPlanFlow = isB2C || isSchoolAdmin;

  // Plan context state (display only — checkout happens after the intake)
  const [selectedPlan, setSelectedPlan] = useState<PlanTier | null>(null);
  const [pricing, setPricing] = useState<Record<string, { intervals?: Array<{ cycle: string; months: number; usd: { cents: number } }> }> | null>(null);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [planContextReady, setPlanContextReady] = useState(false);

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);

  const [showDemoModal, setShowDemoModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<FeatureMapKey | null>(null);
  const demoOpenedAtRef = useRef<number | null>(null);
  const lastCompletionOptionsRef = useRef<{ skipped?: boolean } | undefined>(undefined);
  const submittingRef = useRef(false);
  const isAdvancingRef = useRef(false);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const featureDetailOpacity = useRef(new Animated.Value(0)).current;

  // Track total time-on-screen so we can correlate copy changes with completion velocity.
  const screenMountedAtRef = useRef<number>(Date.now());
  const stepEnteredAtRef = useRef<number>(Date.now());
  const latestStepRef = useRef(0);
  const latestSlideKeyRef = useRef<string | undefined>(undefined);
  const onboardingFinishedRef = useRef(false);

  // Extract plan from URL params
  const getFirst = (value?: string | string[]) => (Array.isArray(value) ? value[0] : value);
  const urlPlan = getFirst(params.plan);

  // Initialize plan context on mount
  useEffect(() => {
    const initPlan = () => {
      // Check URL param first, then localStorage
      const planContext = getOrInitPlanContext(urlPlan, urlPlan ? 'landing' : 'app');
      if (planContext) {
        setSelectedPlan(planContext.planTier);
        trackAuthEvent('onboarding_plan_context_loaded', {
          plan_tier: planContext.planTier,
          source: planContext.source,
        });
      }
      setPlanContextReady(true);
    };

    initPlan();
  }, [urlPlan]);

  // Fetch pricing data
  useEffect(() => {
    const loadPricing = async () => {
      try {
        const data = await billingAPI.pricing();
        if (data?.tiers) {
          const tiersMap: Record<string, { intervals?: Array<{ cycle: string; months: number; usd: { cents: number } }> }> = {};
          for (const tier of data.tiers) {
            tiersMap[tier.tier] = { intervals: tier.intervals as any };
          }
          setPricing(tiersMap);
        }
      } catch (error) {
        console.error('Failed to load pricing:', error);
      }
    };

    if (shouldShowPlanFlow) {
      loadPricing();
    }
  }, [shouldShowPlanFlow]);

  useEffect(() => {
    if (!planContextReady) return;
    trackAuthEvent('onboarding_screen_viewed', {
      has_plan_context: !!selectedPlan,
      plan_tier: selectedPlan,
    });
    // Readiness is a one-way entry boundary. Later plan data changes must not
    // double-count this screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planContextReady]);

  // Format price for display — use the 1mo interval price (USD) as a quick-start reference
  const formatPrice = (tier: PlanTier): string => {
    const tierData = pricing?.[tier];
    if (!tierData?.intervals) return '—';
    const iv = tierData.intervals.find((i) => i.cycle === '1mo') ?? tierData.intervals[0];
    if (!iv?.usd?.cents) return '—';
    const dollars = iv.usd.cents / 100;
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(dollars);
  };

  // Three-slide value journey: promise → proof → membership map + trial.
  // Checkout and intake routing remain unchanged after the final slide.
  const slides: Slide[] = useMemo(() => {
    return [
      {
        type: 'feature',
        icon: 'psychology',
        title: t('onboarding.slides.transcript.title'),
        description: t('onboarding.slides.transcript.description'),
        analyticsKey: 'transcript',
      },
      {
        type: 'demo',
        icon: 'auto-awesome',
        title: t('onboarding.slides.demo.title'),
        description: t('onboarding.slides.demo.description'),
        analyticsKey: 'demo',
      },
      {
        type: 'confirmation',
        icon: 'apps',
        title: t('onboarding.slides.featureMap.title'),
        description: t('onboarding.slides.featureMap.description'),
        analyticsKey: 'feature_map',
      },
    ];
  }, [t]);

  const isLastStep = step === slides.length - 1;
  const currentSlide = slides[step];
  const currentSlideKey = currentSlide?.analyticsKey;

  useEffect(() => {
    latestStepRef.current = step;
    latestSlideKeyRef.current = currentSlideKey;
    stepEnteredAtRef.current = Date.now();
    trackAuthEvent('onboarding_step_viewed', {
      step: step + 1,
      total_steps: slides.length,
      slide_type: currentSlide?.type,
      slide_key: currentSlideKey,
      plan_tier: selectedPlan,
      had_plan_context: !!selectedPlan,
    });
    trackAuthEvent('onboarding_slide_viewed', {
      index: step,
      slide_number: step + 1,
      total_slides: slides.length,
      slide_key: currentSlideKey,
    });
    if (currentSlideKey === 'feature_map') {
      trackAuthEvent('feature_map_slide_viewed', {
        index: step,
        total_slides: slides.length,
      });
    }
    // A logical slide key is the entry boundary. Async plan context changes must
    // not reset duration or emit another view for the same visible slide.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlideKey]);

  useEffect(() => {
    Animated.timing(featureDetailOpacity, {
      toValue: selectedFeature ? 1 : 0,
      duration: ONBOARDING_DISCLOSURE_TRANSITION_MS,
      useNativeDriver: true,
    }).start();
  }, [featureDetailOpacity, selectedFeature]);

  useEffect(() => () => {
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
  }, []);

  useEffect(() => () => {
    if (onboardingFinishedRef.current) return;
    trackAuthEvent('slide_abandoned', {
      index: latestStepRef.current,
      slide_number: latestStepRef.current + 1,
      slide_key: latestSlideKeyRef.current,
      total_duration_seconds: totalDurationSeconds(),
    });
  }, []);

  /** Time-on-step in seconds, for progress/abandon analytics. */
  const stepDurationSeconds = (): number => Math.round((Date.now() - stepEnteredAtRef.current) / 1000);
  /** Time on the whole onboarding screen, for completion-velocity analytics. */
  const totalDurationSeconds = (): number => Math.round((Date.now() - screenMountedAtRef.current) / 1000);

  // ─── Demo modal handlers ─────────────────────────────────────────────
  const handleOpenDemo = useCallback(() => {
    demoOpenedAtRef.current = Date.now();
    setShowDemoModal(true);
    trackAuthEvent('onboarding_demo_opened', {
      step: step + 1,
      time_to_open_seconds: stepDurationSeconds(),
      plan_tier: selectedPlan,
    });
  }, [step, selectedPlan]);

  const handleCloseDemo = useCallback((reason: 'close_button' | 'backdrop' | 'continue') => {
    const openedAt = demoOpenedAtRef.current;
    const dwellSeconds = openedAt ? Math.round((Date.now() - openedAt) / 1000) : 0;
    trackAuthEvent('onboarding_demo_closed', {
      reason,
      dwell_seconds: dwellSeconds,
      step: step + 1,
      plan_tier: selectedPlan,
    });
    demoOpenedAtRef.current = null;
    setShowDemoModal(false);
  }, [step, selectedPlan]);

  // Complete the tour and hand off to the next funnel step (intake first).
  const completeOnboarding = async (opts?: { skipped?: boolean }) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    lastCompletionOptionsRef.current = opts;
    setCompletionError(null);
    setSubmitting(true);
    try {
      await authAPI.completeOnboarding();
      onboardingFinishedRef.current = true;
      if (opts?.skipped) {
        trackAuthEvent('onboarding_skipped', {
          had_plan_context: !!selectedPlan,
          plan_tier: selectedPlan,
          step: step + 1,
          slide_key: currentSlide?.analyticsKey,
          total_duration_seconds: totalDurationSeconds(),
          slides_viewed: step + 1,
          total_slides: slides.length,
          funnel_version: FUNNEL_VERSION,
        });
      } else {
        trackAuthEvent('onboarding_completed', {
          plan_tier: selectedPlan,
          had_plan_context: !!selectedPlan,
          total_duration_seconds: totalDurationSeconds(),
          total_slides: slides.length,
          funnel_version: FUNNEL_VERSION,
        });
      }

      // The tour never starts a checkout. Any landing-page plan context stays
      // stored so the plan-selection screen (now AFTER the intake) preselects it.
      const continueAuth = onContinue ?? refreshAuth;
      await continueAuth();

      // Read the freshly refreshed billing payload rather than the stale
      // context value captured when this handler was created.
      const status = await billingAPI.status().catch(() => null);

      if (requiresIntake && !status?.intake_completed) {
        router.replace(ROUTES.GATED_POST_PAYMENT_ONBOARDING);
        return;
      }

      const hasAccess = hasEffectiveBillingAccess(status, {
        requireBoundedB2CTrial: canAccessIndividualPaywall,
      }) || Boolean(status?.has_purchased_credits);

      if (!hasAccess && shouldShowPlanFlow) {
        router.replace(ROUTES.GATED_PLAN_SELECTION);
      } else {
        router.replace(ROUTES.TABS_ROOT);
      }
    } catch (error: any) {
      const statusCode = error?.response?.status;
      trackAuthEvent('onboarding_completion_failed', {
        error_type: statusCode ? `http_${statusCode}` : 'exception',
      });
      if (statusCode === 401) {
        router.replace(ROUTES.PUBLIC_LOGIN as any);
        return;
      }
      setCompletionError(t('onboarding.completionErrorMessage'));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const beginStepTransition = (nextStep: number) => {
    if (isAdvancingRef.current || submittingRef.current) return;
    isAdvancingRef.current = true;
    setIsAdvancing(true);
    setSelectedFeature(null);
    setStep(nextStep);
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = setTimeout(() => {
      isAdvancingRef.current = false;
      setIsAdvancing(false);
      transitionTimeoutRef.current = null;
    }, ONBOARDING_DISCLOSURE_TRANSITION_MS);
  };

  const handleNext = async () => {
    if (submittingRef.current || isAdvancingRef.current) return;
    trackAuthEvent('onboarding_next_clicked', {
      step: step + 1,
      slide_key: currentSlide?.analyticsKey,
      slide_type: currentSlide?.type,
      time_on_slide_seconds: stepDurationSeconds(),
      is_last_step: isLastStep,
      plan_tier: selectedPlan,
    });
    trackAuthEvent('onboarding_step_completed', {
      index: step,
      step: step + 1,
      slide_key: currentSlide?.analyticsKey,
      total_steps: slides.length,
      time_on_slide_seconds: stepDurationSeconds(),
    });

    if (!isLastStep) {
      beginStepTransition(Math.min(step + 1, slides.length - 1));
      return;
    }

    await completeOnboarding();
  };

  const handleBack = () => {
    if (step === 0 || submittingRef.current || isAdvancingRef.current) return;
    trackAuthEvent('onboarding_back_clicked', {
      from_step: step + 1,
      from_slide_key: currentSlide?.analyticsKey,
      time_on_slide_seconds: stepDurationSeconds(),
      plan_tier: selectedPlan,
    });
    beginStepTransition(Math.max(step - 1, 0));
  };

  const handleSkip = async () => {
    if (submittingRef.current || isAdvancingRef.current) return;
    trackAuthEvent('onboarding_skip_clicked', {
      step: step + 1,
      slide_key: currentSlide?.analyticsKey,
      slide_type: currentSlide?.type,
      time_on_slide_seconds: stepDurationSeconds(),
      total_duration_seconds: totalDurationSeconds(),
      plan_tier: selectedPlan,
    });
    await completeOnboarding({ skipped: true });
  };

  // Get CTA label for current step
  const getCtaLabel = (): string => {
    if (!isLastStep) return t('onboarding.next');

    // Intake now follows the tour for everyone who needs it — the CTA promises
    // questions, never payment.
    if (requiresIntake && !billingStatus?.intake_completed) {
      return t('onboarding.continueToIntake');
    }

    if (shouldShowPlanFlow) {
      return t('onboarding.continueToPlans');
    }

    return t('onboarding.continueToDashboard');
  };

  // Render slide content based on type
  const renderSlideContent = () => {
    // Demo slide — value proof CTA. Opens the sample-report modal.
    if (currentSlide?.type === 'demo') {
      return (
        <View className="items-center">
          <Button
            variant="default"
            size="default"
            onPress={handleOpenDemo}
            accessibilityRole="button"
            accessibilityLabel={t('onboarding.slides.demo.cta')}
            style={({ pressed }) => ({
              minHeight: ONBOARDING_CONTROL_HEIGHT,
              paddingHorizontal: ONBOARDING_SPACING.xl,
              borderRadius: ONBOARDING_RADII.md,
              backgroundColor: pressed ? COLORS.primaryPressed : COLORS.primary,
            })}
          >
            <View className="flex-row items-center">
              <MaterialIcons
                name="play-arrow"
                size={ONBOARDING_SPACING.xl}
                color={COLORS.onPrimary}
              />
              <Text
                style={{
                  ...ONBOARDING_TYPOGRAPHY.button,
                  color: COLORS.onPrimary,
                  marginLeft: ONBOARDING_SPACING.sm,
                }}
              >
                {t('onboarding.slides.demo.cta')}
              </Text>
            </View>
          </Button>
        </View>
      );
    }

    if (currentSlide?.type === 'confirmation') {
      return (
        <View style={{ gap: ONBOARDING_SPACING.lg }}>
          {requiresIntake && !billingStatus?.intake_completed ? (
            <Text
              className="text-center"
              style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.primary }}
            >
              {t('onboarding.slides.featureMap.nextUp')}
            </Text>
          ) : null}

          <View
            style={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              alignItems: 'stretch',
              gap: ONBOARDING_SPACING.sm,
            }}
          >
            {FEATURE_MAP_ITEMS.map((item) => {
              const active = selectedFeature === item.key;
              return (
                <TouchableOpacity
                  key={item.key}
                  onPress={() => {
                    setSelectedFeature(item.key);
                    trackFeatureMapItemTapped(item.key);
                  }}
                  accessibilityRole="button"
                  accessibilityLabel={t(`onboarding.slides.featureMap.items.${item.key}.label`)}
                  accessibilityState={{ selected: active }}
                  style={{
                    flexBasis: isPhone ? '45%' : '22%',
                    flexGrow: 1,
                    minWidth: 0,
                    padding: ONBOARDING_SPACING.md,
                    borderRadius: ONBOARDING_RADII.md,
                    borderWidth: 1,
                    borderColor: active ? COLORS.primary : COLORS.border,
                    backgroundColor: active ? COLORS.primarySubtle : COLORS.surface,
                    alignItems: 'flex-start',
                    gap: ONBOARDING_SPACING.sm,
                  }}
                >
                  <View
                    style={{
                      width: ONBOARDING_SPACING.xxl,
                      height: ONBOARDING_SPACING.xxl,
                      borderRadius: ONBOARDING_RADII.sm,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: active ? COLORS.primary : COLORS.primarySubtle,
                    }}
                  >
                    <MaterialIcons
                      name={item.icon}
                      size={ONBOARDING_SPACING.lg}
                      color={active ? COLORS.onPrimary : COLORS.primary}
                    />
                  </View>
                  <Text
                    style={{
                      ...ONBOARDING_TYPOGRAPHY.bodySmall,
                      color: COLORS.text.primary,
                      fontWeight: '700',
                    }}
                  >
                    {t(`onboarding.slides.featureMap.items.${item.key}.label`)}
                  </Text>
                  <Text
                    style={{
                      ...ONBOARDING_TYPOGRAPHY.caption,
                      color: COLORS.text.secondary,
                    }}
                  >
                    {t(`onboarding.slides.featureMap.items.${item.key}.line`)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>

          <Animated.View
            accessibilityElementsHidden={!selectedFeature}
            importantForAccessibility={selectedFeature ? 'auto' : 'no-hide-descendants'}
            style={{
              minHeight: ONBOARDING_FEATURE_DETAIL_MIN_HEIGHT,
              opacity: featureDetailOpacity,
              borderRadius: ONBOARDING_RADII.md,
              borderWidth: 1,
              borderColor: COLORS.focusRing,
              backgroundColor: COLORS.primarySubtle,
              padding: ONBOARDING_SPACING.lg,
              justifyContent: 'center',
            }}
          >
            {selectedFeature ? (
              <Text style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.primary }}>
                {t(`onboarding.slides.featureMap.items.${selectedFeature}.detail`)}
              </Text>
            ) : null}
          </Animated.View>

          {selectedPlan ? (
            <PlanSummaryCard
              planTier={selectedPlan}
              price={formatPrice(selectedPlan)}
              showFeatures={false}
              compact
              highlighted
            />
          ) : null}

          <View
            style={{
              padding: ONBOARDING_SPACING.lg,
              borderRadius: ONBOARDING_RADII.sm,
              backgroundColor: COLORS.successSubtle,
            }}
          >
            <View className="flex-row items-start justify-center">
              <MaterialIcons
                name="verified-user"
                size={ONBOARDING_SPACING.lg}
                color={COLORS.success}
              />
              <Text
                className="text-center flex-1"
                style={{
                  ...ONBOARDING_TYPOGRAPHY.bodySmall,
                  color: COLORS.text.secondary,
                  marginLeft: ONBOARDING_SPACING.sm,
                }}
              >
                {t('onboarding.slides.featureMap.trialConfirmation')}
              </Text>
            </View>
          </View>
        </View>
      );
    }

    // Standard feature slides need no extra body beyond the shared title shell.
    return null;
  };

  return (
    <>
      <OnboardingStepLayout
        key={currentSlideKey}
        step={step + 1}
        totalSteps={slides.length}
        journeyPart="tour"
        helperText={t('onboarding.infoMessage')}
        onBack={step > 0 ? handleBack : undefined}
        headerRightSlot={
          <TouchableOpacity
            onPress={handleSkip}
            disabled={submitting || isAdvancing}
            accessibilityRole="button"
            accessibilityLabel={t('onboarding.skipTour')}
            accessibilityState={{ disabled: submitting || isAdvancing }}
            style={{
              minHeight: ONBOARDING_CONTROL_HEIGHT,
              minWidth: ONBOARDING_CONTROL_HEIGHT,
              justifyContent: 'center',
            }}
          >
            <Text
              style={{
                ...ONBOARDING_TYPOGRAPHY.bodySmall,
                color: submitting || isAdvancing ? COLORS.text.disabled : COLORS.primary,
                fontWeight: '500',
              }}
            >
              {t('onboarding.skipTour')}
            </Text>
          </TouchableOpacity>
        }
        icon={currentSlide?.icon}
        title={currentSlide?.title}
        subtitle={currentSlide?.description}
        primaryCta={{
          label: submitting ? t('common.loading') : getCtaLabel(),
          onPress: handleNext,
          disabled: submitting || isAdvancing,
          loading: submitting,
        }}
      >
        {renderSlideContent()}
        {completionError ? (
          <View
            style={{
              borderRadius: ONBOARDING_RADII.sm,
              padding: ONBOARDING_SPACING.lg,
              backgroundColor: COLORS.errorSubtle,
            }}
            accessibilityRole="alert"
            accessibilityLiveRegion="polite"
          >
            <View className="flex-row items-start">
              <MaterialIcons
                name="error-outline"
                size={ONBOARDING_SPACING.xl}
                color={COLORS.error}
              />
              <View style={{ marginLeft: ONBOARDING_SPACING.md, flex: 1 }}>
                <Text
                  style={{
                    ...ONBOARDING_TYPOGRAPHY.body,
                    color: COLORS.error,
                    fontWeight: '600',
                    marginBottom: ONBOARDING_SPACING.xs,
                  }}
                >
                  {t('onboarding.completionErrorTitle')}
                </Text>
                <Text
                  style={{
                    ...ONBOARDING_TYPOGRAPHY.bodySmall,
                    color: COLORS.text.secondary,
                    marginBottom: ONBOARDING_SPACING.md,
                  }}
                >
                  {completionError}
                </Text>
                <TouchableOpacity
                  onPress={() => completeOnboarding(lastCompletionOptionsRef.current)}
                  disabled={submitting}
                  accessibilityRole="button"
                  accessibilityLabel={t('common.retry')}
                  accessibilityState={{ disabled: submitting, busy: submitting }}
                  style={{
                    minHeight: ONBOARDING_CONTROL_HEIGHT,
                    minWidth: ONBOARDING_CONTROL_HEIGHT,
                    alignSelf: 'flex-start',
                    justifyContent: 'center',
                  }}
                >
                  <Text
                    style={{
                      ...ONBOARDING_TYPOGRAPHY.button,
                      color: submitting ? COLORS.text.disabled : COLORS.primary,
                    }}
                  >
                    {t('common.retry')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ) : null}
      </OnboardingStepLayout>

      <SampleReportViewer
        visible={showDemoModal}
        surface="onboarding_slide"
        primaryAction="continue"
        language={language}
        onClose={() => handleCloseDemo('close_button')}
        onPrimaryPress={() => handleCloseDemo('continue')}
      />
    </>
  );
}
